import { MobileDrawer } from '@wix/wix-ui-tpa/cssVars';
import classNames from 'classnames';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ConfigContext } from '../../../../contexts/config-context';
import { useId } from '../../../../hooks/use-id';
import { CoreDropdownContent } from '../../../autocomplete/wut/WixUiCoreComponents/components/CoreDropdownContent';
import { OptionFactory } from '../../../autocomplete/wut/WixUiCoreComponents/components/CoreDropdownOption';
import { toLongCountryCode } from '../phone-input-utils';
import { CountryOption } from './country-option';
import { classes } from './drawer-with-search.st.css';
import { FlagButton } from './flag-button';
import { Search } from './search';
export const DrawerWithSearch = ({ className, theme, countryCode, countries, onSelect, searchPhrase, onSearch, onKeyDown, readOnly, ariaLabel })=>{
    const flagButtonRef = useRef(null);
    const dropdownRef = useRef(null);
    const longCountryCode = useMemo(()=>toLongCountryCode(countryCode), [
        countryCode
    ]);
    const [isOpen, setIsOpen] = useState(false);
    const { comboboxProps, listboxProps, setActiveDescendant } = useComboxbox();
    const options = useMemo(()=>countries.map(createOption), [
        countries
    ]);
    const initialSelectedIds = useMemo(()=>countryCode ? [
            countryCode
        ] : [], [
        countryCode
    ]);
    const firstCountryCode = countries[0]?.shortCountryCode;
    const focusOnOption = searchPhrase ? firstCountryCode : countryCode ?? firstCountryCode;
    const handleExpandChange = useCallback((open)=>{
        setIsOpen(readOnly ? false : open);
        onSearch('');
    }, [
        onSearch,
        readOnly
    ]);
    const handleKeydown = useCallback((event)=>{
        switch(event.key){
            case 'Tab':
                event.preventDefault();
                handleExpandChange(false);
                break;
            case 'Escape':
                event.preventDefault();
                handleExpandChange(false);
                flagButtonRef.current?.focus();
                break;
            case 'Enter':
                event.preventDefault();
                handleExpandChange(false);
                const _countryCode = dropdownRef.current?.getSelectedOption()?.id;
                _countryCode && onSelect(_countryCode);
                break;
            case 'ArrowUp':
            case 'ArrowDown':
                dropdownRef.current?.onKeyDown(event.key, event);
                break;
        }
        onKeyDown(event);
    }, [
        onKeyDown,
        handleExpandChange,
        onSelect
    ]);
    const handleSelect = useCallback((option)=>{
        const _countryCode = option?.id;
        handleExpandChange(false);
        _countryCode && onSelect(_countryCode);
    }, [
        onSelect,
        handleExpandChange
    ]);
    const handleSearch = useCallback((event)=>onSearch(event.target.value), [
        onSearch
    ]);
    const handleOptionHover = useCallback((option)=>{
        option && setActiveDescendant(option._DOMid);
    }, [
        setActiveDescendant
    ]);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(FlagButton, {
        readOnly: readOnly,
        arrowDirection: isOpen ? 'up' : 'down',
        longCountryCode: longCountryCode,
        onClick: ()=>handleExpandChange(true),
        ariaLabel: ariaLabel
    }), isOpen && /*#__PURE__*/ React.createElement(ConfigContext.Consumer, null, (config)=>{
        const Container = config?.PortalContainer ?? React.Fragment;
        return /*#__PURE__*/ React.createElement(MobileDrawer, {
            appendToBody: true,
            isOpen: isOpen,
            onRequestClose: ()=>handleExpandChange(false),
            className: classNames(// compId as className adds form app style vars
            config?.compId, // compId_ooi as className adds form app style vars in Harmony sites
            // TODO: Might be possible to remove when https://wix.atlassian.net/browse/TB-14333 is fixed
            `${config?.compId}_ooi`, classes.drawer)
        }, /*#__PURE__*/ React.createElement(Container, null, /*#__PURE__*/ React.createElement(CoreDropdownContent, {
            "data-hook": "dropdown-content",
            optionsContainerId: listboxProps.id,
            role: listboxProps.role,
            className: classes.content,
            ref: dropdownRef,
            selectedIds: initialSelectedIds,
            options: options,
            onOptionClick: handleSelect,
            onOptionHover: handleOptionHover,
            focusOnOption: focusOnOption,
            fixedHeader: /*#__PURE__*/ React.createElement("div", {
                className: classes.searchContainer
            }, /*#__PURE__*/ React.createElement(Search, {
                ...comboboxProps,
                className: className,
                tabIndex: isOpen ? -1 : 0,
                theme: theme,
                value: searchPhrase,
                onChange: handleSearch,
                onKeyDown: handleKeydown
            }))
        })));
    }));
};
function createOption({ longCountryCode, shortCountryCode, callingCode, countryName }) {
    return OptionFactory.create({
        id: shortCountryCode,
        render: ()=>/*#__PURE__*/ React.createElement(CountryOption, {
                callingCode: callingCode,
                longCountryCode: longCountryCode,
                countryName: countryName,
                clip: false
            })
    });
}
function useComboxbox() {
    const listboxId = useId();
    const [activeDescendant, setActiveDescendant] = useState();
    const comboboxProps = {
        role: 'combobox',
        'aria-autocomplete': 'list',
        'aria-haspopup': 'listbox',
        'aria-expanded': true,
        'aria-owns': listboxId,
        'aria-activedescendant': activeDescendant
    };
    const listboxProps = {
        role: 'listbox',
        id: listboxId
    };
    return {
        comboboxProps,
        listboxProps,
        setActiveDescendant
    };
}

//# sourceMappingURL=drawer-with-search.js.map